import { forwardRef, useRef } from 'react'
import { tokenizeHashtags } from '@/lib/hashtags'

export interface HighlightedTextareaProps {
  value: string
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void
  placeholder?: string
  maxLength?: number
  rows?: number
  readOnly?: boolean
  /** Dim the field (e.g. when gated). Styling only — does not block input. */
  dimmed?: boolean
  /** Red border / focus ring for validation errors. */
  error?: boolean
  id?: string
  tabIndex?: number
  /** Applied to the wrapper, so callers can position overlays/counters over it. */
  className?: string
  'aria-label'?: string
  'aria-describedby'?: string
  'aria-invalid'?: boolean
  'aria-disabled'?: boolean
}

// The backdrop and the textarea MUST share this box model so the highlighted
// glyphs line up exactly under the (transparent) editable text. text-base (16px)
// also avoids iOS Safari's focus auto-zoom. `scrollbar-gutter: stable` makes
// BOTH layers always reserve the vertical-scrollbar gutter, so when a long note
// overflows and the textarea shows a classic scrollbar (Windows/Edge), the
// backdrop reserves the same width and the two wrap identically — keeping the
// caret glued to the glyphs. No-op on overlay-scrollbar platforms (e.g. macOS).
const SHARED_BOX =
  'block w-full rounded-xl border px-4 py-3 text-base leading-relaxed resize-none box-border whitespace-pre-wrap break-words [scrollbar-gutter:stable]'

/**
 * A textarea whose `#hashtag` tokens are highlighted inline (Instagram-style),
 * implemented as a transparent textarea layered over a highlighted backdrop.
 * The textarea remains the single source of truth — `value` stays plain text;
 * the backdrop is purely presentational.
 *
 * Contrast: neon-cyan (#00e5ff) on bg-navy-light (#132f4c) = 8.87:1 (WCAG AA).
 * bg-navy-light is a literal color (not a theme variable), so the field looks
 * identical in both the primary and secondary themes.
 */
export const HighlightedTextarea = forwardRef<HTMLTextAreaElement, HighlightedTextareaProps>(
  function HighlightedTextarea(
    {
      value,
      onChange,
      onKeyDown,
      placeholder,
      maxLength,
      rows = 4,
      readOnly = false,
      dimmed = false,
      error = false,
      id,
      tabIndex,
      className = '',
      'aria-label': ariaLabel,
      'aria-describedby': ariaDescribedBy,
      'aria-invalid': ariaInvalid,
      'aria-disabled': ariaDisabled,
    },
    ref,
  ): JSX.Element {
    const backdropRef = useRef<HTMLDivElement>(null)
    const segments = tokenizeHashtags(value)

    const handleScroll = (e: React.UIEvent<HTMLTextAreaElement>) => {
      const backdrop = backdropRef.current
      if (backdrop) {
        backdrop.scrollTop = e.currentTarget.scrollTop
        backdrop.scrollLeft = e.currentTarget.scrollLeft
      }
    }

    return (
      <div className={`relative ${dimmed ? 'opacity-60' : ''} ${className}`.trim()}>
        <div
          ref={backdropRef}
          aria-hidden="true"
          data-testid="hashtag-backdrop"
          className={`${SHARED_BOX} pointer-events-none absolute inset-0 select-none overflow-hidden border-transparent bg-navy-light text-white`}
        >
          {segments.map((segment, index) =>
            segment.isHashtag ? (
              <span key={index} className="font-medium text-neon-cyan">
                {segment.text}
              </span>
            ) : (
              <span key={index}>{segment.text}</span>
            ),
          )}
          {/* Give the trailing empty line height so it matches the textarea. */}
          {value.endsWith('\n') ? '​' : null}
        </div>
        <textarea
          ref={ref}
          id={id}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onScroll={handleScroll}
          placeholder={placeholder}
          rows={rows}
          maxLength={maxLength}
          readOnly={readOnly}
          tabIndex={tabIndex}
          className={`${SHARED_BOX} relative bg-transparent text-transparent caret-white placeholder-gray-500 transition-colors focus:outline-none focus:ring-2 focus:ring-neon-cyan focus:ring-offset-2 focus:ring-offset-navy ${
            error
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
              : 'border-navy-light focus:border-neon-cyan'
          }`}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid}
          aria-disabled={ariaDisabled}
        />
      </div>
    )
  },
)
